import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Loader, ModalComponent, Switch, useQueryFetch, VeilV2 } from 'sonata-react-components';
import {
  CampaignPlanPacingProgress,
  InsertionOrderPacingProgress,
  LineItemPacingProgressTotal,
  MultiFlightEntityType
} from 'sonata-static-storage/lib/common/common.types';
import {
  getCampaignPlan,
  getCampaignPlanPacing
} from 'sonata-static-storage/lib/entities/campaignPlan/campaignPlan.service';
import {
  getInsertionOrder,
  getInsertionOrderPacing
} from 'sonata-static-storage/lib/entities/insertionOrder/insertionOrder.service';
import { getLineItemPacing } from 'sonata-static-storage/lib/entities/lineItem/lineItem.service';

import { getLineItem, getLineItems } from '@/services/lineItem.service';
import { CampaignPlanPacingStatus } from './CampaignPlanPacingStatus/CampaignPlanPacingStatus';
import { EntityInformationalHeader } from './EntityInformationalHeader/EntityInformationalHeader';
import { InsertionOrderPacingStatus } from './InsertionOrderPacingStatus/InsertionOrderPacingStatus';
import { LineItemPacingStatus } from './LineItemPacingStatus/LineItemPacingStatus';
import { PacingColorLegend } from './PacingColorLegend/PacingColorLegend';

import './pacingStatusModal.scss';

import type { InsertionOrderEntity, LineItem } from 'sonata-static-storage/lib/entities';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { LineItemPacing } from './EntityPacingStatus/PacingStatusTable/PacingStatusTable';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  entityId: string;
  entityType: MultiFlightEntityType;
  isOpen: boolean;
  onClose: () => void;
  theme?: 'dark' | 'light';
  userId: string;
};

export function PacingStatusModal({
  currencyInfo,
  entityId,
  entityType,
  isOpen,
  onClose,
  theme = 'light',
  userId
}: Readonly<Props>): JSX.Element {
  const { formatMessage } = useIntl();

  const tokens: Record<string, any> = {
    entity: {},
    entityPacing: {},
    parentEntity: {}
  };

  useEffect(() => {
    return () => {
      Object.keys(tokens).forEach(tokenKey => {
        tokens[tokenKey]?.cancel?.();
      });
    };
  }, []);

  const {
    data: entity,
    isLoading: entityIsLoading,
    status: entityStatus
  } = useQueryFetch<CampaignPlan | InsertionOrderEntity | Partial<LineItem>>(
    ['entity', entityType, entityId],
    async () => {
      if (entityType === 'lineItem') {
        return getLineItem(userId, entityId);
      }
      if (entityType === 'insertionOrder') {
        return getInsertionOrder(userId, entityId, undefined, tokens.entity);
      }
      return getCampaignPlan(userId, entityId, tokens.entity);
    },
    {
      cacheTime: 0
    }
  );

  const { data: entityPacing, isLoading: entityPacingIsLoading } = useQueryFetch<
    InsertionOrderPacingProgress | CampaignPlanPacingProgress | LineItemPacingProgressTotal | null
  >(
    ['pacing', entityType, entityId],
    async () => {
      if (entityType === 'lineItem') {
        return getLineItemPacing(userId, entityId, tokens.entityPacing);
      }
      if (entityType === 'insertionOrder') {
        return getInsertionOrderPacing(userId, entityId, tokens.entityPacing);
      }
      return getCampaignPlanPacing(userId, entityId, tokens.entityPacing);
    },
    {
      cacheTime: 0
    }
  );

  const { data: parentEntity, isLoading: parentEntityIsLoading } = useQueryFetch<
    CampaignPlan | InsertionOrderEntity | undefined
  >(
    ['parentEntity', entityType === 'insertionOrder' ? 'campaignPlan' : 'lineItem', entityId],
    async () => {
      if (entityType === 'lineItem') {
        const insertionOrderId = (entity as Partial<LineItem>)?.insertionOrder;
        return insertionOrderId
          ? getInsertionOrder(userId, insertionOrderId, undefined, tokens.parentEntity)
          : undefined;
      }
      if (entityType === 'insertionOrder') {
        return getCampaignPlan(userId, (entity as InsertionOrderEntity)?.entity.campaignPlan, tokens.parentEntity);
      }
      if (entityType === 'campaignPlan') {
        return undefined;
      }
    },
    {
      cacheTime: 0,
      enabled: entityStatus === 'success'
    }
  );

  const pacedLineItemIds =
    entityType === 'insertionOrder'
      ? [
          ...new Set(
            (entityPacing as InsertionOrderPacingProgress | null)?.lineItemGoals?.flatMap(goal =>
              (goal.lineItems ?? []).map(({ lineItemId }) => lineItemId)
            )
          )
        ]
      : [];

  // One batched request per modal open; a failure leaves data undefined so every row stays visible.
  const { data: lineItemFields, isLoading: lineItemFieldsIsLoading } = useQueryFetch<
    Record<string, { deleted?: boolean }>
  >(
    ['lineItemFields', entityId],
    async () => {
      const { items } = await getLineItems(userId, {
        fields: ['deleted'],
        ids: pacedLineItemIds,
        includeDeleted: true,
        // the list endpoint paginates; ask for every id in one page
        itemsPerPage: String(pacedLineItemIds.length)
      });
      return Object.fromEntries(items.map(item => [item._id, { deleted: item.deleted }]));
    },
    {
      cacheTime: 0,
      enabled: entityType === 'insertionOrder' && pacedLineItemIds.length > 0
    }
  );

  const isLoading =
    entityIsLoading ||
    entityPacingIsLoading ||
    lineItemFieldsIsLoading ||
    (entityType === 'insertionOrder' && parentEntityIsLoading);

  const getEntityName = () => {
    switch (entityType) {
      case 'campaignPlan':
        return (entity as CampaignPlan)?.name;
      case 'insertionOrder':
        return (entity as InsertionOrderEntity)?.entity?.name;
      case 'lineItem':
        return (entity as LineItem)?.name;
      default:
        return '';
    }
  };

  const getParentEntityName = () => {
    switch (entityType) {
      case 'insertionOrder':
        return (parentEntity as CampaignPlan)?.name;
      case 'lineItem':
        return (parentEntity as InsertionOrderEntity)?.entity?.name;
      default:
        return undefined;
    }
  };

  return (
    <ModalComponent
      id={`${entityType}_${entityId}_modal`}
      isModalOpened={isOpen}
      key={`${entityType}_${entityId}_modal`}
      maxHeight="85vh"
      onClose={onClose}
      portalId="entity-pacing-modal"
      theme={theme}
      title={formatMessage({
        defaultMessage: 'Pacing Status',
        id: 'pacingStatusModal.title'
      })}
      width="1343px"
    >
      <ModalComponent.ModalContent>
        <div className="pacingStatusModal">
          <Switch>
            <Switch.Case condition={isLoading}>
              <VeilV2 className="pacingStatusModal__veil" id={`pacingStatusModal_${entityType}_${entityId}_veil`}>
                <Loader
                  className="pacingStatusModal__veilSpinner"
                  id={`pacingStatusModal_${entityType}_${entityId}_spinner`}
                  type="landing"
                  variant={theme === 'dark' ? 'li' : 'default'}
                />
              </VeilV2>
            </Switch.Case>
            <Switch.Default>
              <div className="pacingStatusModal__header">
                <EntityInformationalHeader
                  entityName={getEntityName()}
                  entityParentName={getParentEntityName()}
                  entityType={entityType}
                />
                <PacingColorLegend />
              </div>
              <Switch>
                <Switch.Case condition={entityType === 'insertionOrder'}>
                  <InsertionOrderPacingStatus
                    currencyInfo={currencyInfo}
                    entityPacing={entityPacing as InsertionOrderPacingProgress | null}
                    lineItemFields={lineItemFields}
                  />
                </Switch.Case>
                <Switch.Case condition={entityType === 'campaignPlan'}>
                  <CampaignPlanPacingStatus
                    currencyInfo={currencyInfo}
                    entityPacing={entityPacing as CampaignPlanPacingProgress | null}
                  />
                </Switch.Case>
                <Switch.Case condition={entityType === 'lineItem'}>
                  <LineItemPacingStatus
                    currencyInfo={currencyInfo}
                    entityPacing={entityPacing as LineItemPacing | null}
                  />
                </Switch.Case>
              </Switch>
            </Switch.Default>
          </Switch>
        </div>
      </ModalComponent.ModalContent>
    </ModalComponent>
  );
}
